import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Copy, Check, Link2, ExternalLink, Trash2, Clock, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { paymentLinksApi } from '@/lib/api'
import { getApiErrorMessage, formatCurrency, formatDate } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type { PaymentLink, PaymentLinkStatus } from '@/types'

interface PaymentLinkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  invoiceId: string
  invoiceNumber: string
  balanceDue: number
  currency: string
}

const statusColors: Record<PaymentLinkStatus, string> = {
  ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  USED: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  EXPIRED: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
}

export default function PaymentLinkDialog({
  open,
  onOpenChange,
  invoiceId,
  invoiceNumber,
  balanceDue,
  currency,
}: PaymentLinkDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [expiresInDays, setExpiresInDays] = useState('7')
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [linkToCancel, setLinkToCancel] = useState<PaymentLink | null>(null)
  const [newlyCreatedLink, setNewlyCreatedLink] = useState<PaymentLink | null>(null)

  // Fetch existing payment links for this invoice
  const { data: existingLinks = [] } = useQuery({
    queryKey: ['payment-links', invoiceId],
    queryFn: () => paymentLinksApi.getByInvoice(invoiceId),
    enabled: open,
  })

  const createMutation = useMutation({
    mutationFn: paymentLinksApi.create,
    onSuccess: (link) => {
      queryClient.invalidateQueries({ queryKey: ['payment-links', invoiceId] })
      setNewlyCreatedLink(link)
      toast.success(t('payments.linkCreated', 'Payment link created successfully'))
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('payments.createLinkError', 'Failed to create payment link'))),
  })

  const cancelMutation = useMutation({
    mutationFn: paymentLinksApi.cancel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-links', invoiceId] })
      toast.success(t('payments.linkCancelled', 'Payment link cancelled'))
      setCancelDialogOpen(false)
      setLinkToCancel(null)
    },
    onError: (error) => toast.error(getApiErrorMessage(error, t('payments.cancelLinkError', 'Failed to cancel payment link'))),
  })

  const handleCreateLink = () => {
    createMutation.mutate({
      invoice_id: invoiceId,
      expires_in_days: parseInt(expiresInDays) || 7,
    })
  }

  const handleCopyLink = async (link: PaymentLink) => {
    try {
      await navigator.clipboard.writeText(link.payment_url)
      setCopiedLinkId(link.link_id)
      toast.success(t('payments.linkCopied', 'Payment link copied to clipboard'))
      setTimeout(() => setCopiedLinkId(null), 2000)
    } catch {
      toast.error(t('payments.copyFailed', 'Failed to copy link'))
    }
  }

  const activeLinks = existingLinks.filter(l => l.status === 'ACTIVE')
  const hasActiveLink = activeLinks.length > 0

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              {t('payments.paymentLinks', 'Payment Links')}
            </DialogTitle>
            <DialogDescription>
              {t('payments.paymentLinksDescription', 'Generate secure payment links for invoice')} #{invoiceNumber}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Balance Due Info */}
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <span className="text-sm text-muted-foreground">{t('invoices.balanceDue', 'Balance Due')}</span>
              <span className="font-semibold">{formatCurrency(balanceDue, currency)}</span>
            </div>

            {/* Newly Created Link */}
            {newlyCreatedLink && (
              <div className="p-4 border-2 border-green-500 rounded-lg bg-green-50 dark:bg-green-950 space-y-3">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <Check className="h-5 w-5" />
                  <span className="font-medium">{t('payments.linkReady', 'Payment Link Ready!')}</span>
                </div>
                <div className="flex gap-2">
                  <Input value={newlyCreatedLink.payment_url} readOnly className="font-mono text-sm" />
                  <Button onClick={() => handleCopyLink(newlyCreatedLink)} variant="outline">
                    {copiedLinkId === newlyCreatedLink.link_id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <Button onClick={() => window.open(newlyCreatedLink.payment_url, '_blank')} variant="outline">
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('payments.shareLink', 'Share this link with your client to collect payment.')}
                </p>
              </div>
            )}

            {/* Create New Link Section */}
            {!hasActiveLink && !newlyCreatedLink && (
              <div className="space-y-3 p-4 border rounded-lg">
                <h4 className="font-medium">{t('payments.createNewLink', 'Create New Payment Link')}</h4>
                <div className="flex items-end gap-3">
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="expires">{t('payments.expiresIn', 'Expires In (Days)')}</Label>
                    <Input
                      id="expires"
                      type="number"
                      min="1"
                      max="90"
                      value={expiresInDays}
                      onChange={(e) => setExpiresInDays(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleCreateLink} disabled={createMutation.isPending}>
                    {createMutation.isPending ? 'Creating...' : t('payments.generateLink', 'Generate Link')}
                  </Button>
                </div>
              </div>
            )}

            {/* Existing Links */}
            {existingLinks.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium">{t('payments.existingLinks', 'Existing Payment Links')}</h4>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {existingLinks.map((link) => (
                    <div
                      key={link.link_id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge className={statusColors[link.status]}>{link.status}</Badge>
                          <span className="text-sm font-medium">{formatCurrency(link.amount, link.currency)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {link.expires_at ? (
                            <span>{t('payments.expiresOn', 'Expires')}: {formatDate(link.expires_at)}</span>
                          ) : (
                            <span>{t('payments.noExpiry', 'No expiry')}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {link.status === 'ACTIVE' && (
                          <>
                            <Button size="icon" variant="ghost" onClick={() => handleCopyLink(link)}>
                              {copiedLinkId === link.link_id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => window.open(link.payment_url, '_blank')}>
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => { setLinkToCancel(link); setCancelDialogOpen(true) }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Warning if active link exists */}
            {hasActiveLink && !newlyCreatedLink && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800 dark:text-yellow-200">
                    {t('payments.activeLinkExists', 'Active payment link exists')}
                  </p>
                  <p className="text-yellow-700 dark:text-yellow-300">
                    {t('payments.cancelToCreate', 'Cancel the existing link to create a new one.')}
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { onOpenChange(false); setNewlyCreatedLink(null) }}>
              {t('common.close', 'Close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Link Confirmation */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('payments.cancelLink', 'Cancel Payment Link?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('payments.cancelLinkWarning', 'This will invalidate the payment link. Your client will no longer be able to use it to make a payment.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => linkToCancel && cancelMutation.mutate(linkToCancel.link_id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelMutation.isPending ? 'Cancelling...' : t('payments.cancelLink', 'Cancel Link')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
